import { useState, useCallback, useMemo, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styles from './MyCourses.module.scss';
import {
  SllCourse,
  SllLesson,
  useSllGetCaregiverLessonsQuery,
} from '@api/graphql-codegen';
import Spinner from 'src/components/spinner/Spinner';
import './customStyle.css';
import {
  useAnalytics,
  Events,
  useQueryParams,
  useRolesPermissions,
  useUser
} from '@hooks';
import { MyCourseEnum, UserRoleEnum } from '@types';
import Header from 'src/components/header/Header';
import { colors, darkColors } from './MyCourses';
import LessonProgressModal from '@src/components/lesson-progress-modal/LessonProgressModal';
import CaregiverNavLayout from './navLayout/CaregiverNavLayout';
import NewCoverCard from '../lessonlibrary/gradeItem/NewCoverCard/NewCoverCard';
import { caregiverLessonsOrder } from '@src/constants';
import { normalizeMixpanelProgramType } from '@src/utils/mixpanelProgramType';
import FooterPage from '@src/components/footer-page/FooterPage';


function Caregiver({ isPA }: { isPA?: boolean }) {
  const { trackEvent } = useAnalytics();
  const history = useHistory();
  const params = useQueryParams();
  const lessonId = params.get('lid');
  const [showLesson, setShowLesson] = useState(false);
  const [selLesson, setSelLesson] = useState<SllLesson | null>(null);
  const [selMyCourse, setSelMyCourse] = useState<SllCourse | undefined>(undefined);
  const [user] = useUser();

  const isSuperAdmin = useRolesPermissions([UserRoleEnum.SuperAdmin]);
  const isAUuser = useMemo(() => {return user?.id?.startsWith("AU_") ? true : false}, [user?.id]);

  const { data: sllLessons, loading } = useSllGetCaregiverLessonsQuery({fetchPolicy: 'network-only'});

  const myCourses: SllCourse[] = useMemo(() => {
    const newCourses: SllCourse[] = [];
    (sllLessons?.sllGetCaregiverLessons || []).map((lesson) => {
      if (lesson?.ageGroup) {
        const currentCourse = newCourses.find(course => course.name === lesson.ageGroup);
        if (!newCourses?.length || !currentCourse) {
          const color = colors[Math.floor(Math.random() * colors.length)];
          const foundIdx = colors.findIndex(el => el === color);
          const darkColor = darkColors[foundIdx];
          newCourses.push({ name: lesson.ageGroup, color, darkColor, id: lesson.id, lessonIds: [lesson.id] });
        } else if (currentCourse.lessonIds?.length) {
          currentCourse.lessonIds.push(lesson.id);
        }
      }
      return null;
    });

    return newCourses;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sllLessons?.sllGetCaregiverLessons?.length]);

  useEffect(() => {
    if (!lessonId || lessonId === selLesson?.id) return;
    const lesson = sllLessons?.sllGetCaregiverLessons?.find(l => l.id && l.id === lessonId) as SllLesson
    if (lesson?.id) {
      history.replace(`/${isPA ? 'parent/dashboard' : 'caregiver'}?lid=${lesson?.id}`);
      setSelLesson(lesson);
      setShowLesson(true);
      trackEvent(Events.actions.standaloneLessonOpen, {
        lesson_name: lesson.name,
        grade: lesson.ageGroup || lesson.gradeLevel,
        isCaregiver: true,
        skill: lesson.skill,
        ...(lesson.collections && lesson.collections.length && {
          collection_name: lesson.collections.filter(c => c?.collectionType === 'Theme')[0]?.name,
        }),
        ...(lesson.multiCollections && lesson.multiCollections.length && {
          program_name: normalizeMixpanelProgramType(lesson.multiCollections[0]?.programType),
          multi_collection_name: lesson.multiCollections.map(mc => mc?.name),
        }),
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId, selLesson?.id, sllLessons?.sllGetCaregiverLessons?.length, trackEvent, isPA, user])

  useEffect(() => {
    if (!selMyCourse) {
      const noLessonCourse = myCourses.find(course => course.lessonIds?.length === 0)
      setSelMyCourse(noLessonCourse);
    }
  }, [myCourses, selMyCourse, setSelMyCourse])

  const handleOpenLesson = useCallback((e: React.MouseEvent<HTMLDivElement>, lesson: SllLesson) => {
    e.preventDefault();
    e.stopPropagation();
    if (lesson) {
      setSelLesson(lesson);
      history.replace(`/${isPA ? 'parent/dashboard' : 'caregiver'}?lid=${lesson?.id}`);
      setShowLesson(true);
      trackEvent(Events.actions.standaloneLessonOpen, {
        lesson_name: lesson.name,
        grade: lesson.ageGroup || lesson.gradeLevel,
        isCaregiver: true,
        skill: lesson.skill,
        ...(lesson.collections && lesson.collections.length && {
          collection_name: lesson.collections.filter(c => c?.collectionType === 'Theme')[0]?.name,
        }),
        ...(lesson.multiCollections && lesson.multiCollections.length && {
          program_name: normalizeMixpanelProgramType(lesson.multiCollections[0]?.programType),
          multi_collection_name: lesson.multiCollections.map(mc => mc?.name),
        }),
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    }
  }, [history, isPA, trackEvent, user]);

  const closeModal = useCallback(() => {
    setShowLesson(false);
    setSelLesson(null);
    history.replace(`/${isPA ? 'parent/dashboard' : 'caregiver'}`);
  }, [history, isPA]);

  const lessonList = useMemo(() => {
    if (!sllLessons?.sllGetCaregiverLessons) return [];
    const sortedLessons = [...sllLessons?.sllGetCaregiverLessons || []].sort((a, b) => {
      const indexA = caregiverLessonsOrder.indexOf(a.name.toLowerCase());
      const indexB = caregiverLessonsOrder.indexOf(b.name.toLowerCase());
  
      return (indexA === -1 ? caregiverLessonsOrder.length : indexA) - 
         (indexB === -1 ? caregiverLessonsOrder.length : indexB);
    });
    return sortedLessons;

  }, [sllLessons?.sllGetCaregiverLessons])
  return (
    <>
      {isPA ? null : <Header />}
      <div className={styles.caregiverWrapper}>
        {loading ? <div className={styles.spinner}><Spinner /></div> : <>
        <div className={styles.page}>
          <div className={styles.caregiverView}>
            <div style={{width: '100%', height: '100%'}}>
              <CaregiverNavLayout isUser={!isSuperAdmin && !isPA}/>
              <div className={styles.mainLessonsView}>
                <div className={styles.mcBlock}>
                  <div className={styles.unitsDivWithFourGradient}>
                    {lessonList.map((lesson, index) => {
                      return (
                        <NewCoverCard
                          key={index}
                          content={lesson as any}
                          country={lesson.country ?? ''}
                          handleOpenContent={handleOpenLesson}
                          isAUuser={isAUuser}
                          isCaregiver={true}
                      />
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
          <FooterPage />
        </>}
      </div>
      {(showLesson && selLesson && lessonId) && <LessonProgressModal lessonId={lessonId || ''} open={showLesson} onClose={closeModal} lesson={selLesson} lessonlist={sllLessons?.sllGetCaregiverLessons ?? []} courseMode={MyCourseEnum.Courses} course={selMyCourse} isParentApp={isPA} />}
    </>
  );
}

export default Caregiver;
